import React from "react";
import { ShieldCheck, Search, Calendar, MessagesSquare, Sparkles, Users, TrendingDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Eight-step BHEGA journey — wording preserved verbatim per user spec.
 * Design: left-aligned numbered timeline with a vertical accent rail and
 * card-style step blocks. Mobile collapses to a single column; on desktop
 * the rail sits in the gutter so the step copy reads in a clean column.
 */
const STEPS = [
  {
    icon: <ShieldCheck size={20} />,
    title: "Sign Up Securely",
    body: "Create your free BHEGA account. Your name and phone number remain private and are never shared with other buyers.",
  },
  {
    icon: <Search size={20} />,
    title: "Choose What You Want to Buy",
    body: "Search and select the product you're planning to purchase.",
  },
  {
    icon: <span className="text-lg leading-none" aria-hidden>✊</span>,
    title: "Hit the BHEGA Button",
    body: "Click BHEGA to express your genuine buying intent.",
  },
  {
    icon: <Calendar size={20} />,
    title: "Select Your Buying Month",
    body: "Choose the month in which you intend to make your purchase. This helps connect you with buyers on the same timeline.",
  },
  {
    icon: <MessagesSquare size={20} />,
    title: "Join Your Negotiation Room",
    body: "A dedicated chat room is created for everyone planning to buy the same product in the same month.",
  },
  {
    icon: <Sparkles size={20} />,
    title: "Discuss & Compare",
    body: "Chat with fellow buyers about product features, pricing, seller recommendations, offers, financing options, and buying experiences.",
  },
  {
    icon: <Users size={20} />,
    title: "Organize Together",
    body: "Plan an online meeting, meet at a café, or coordinate a visit to the seller's showroom as a group.",
  },
  {
    icon: <TrendingDown size={20} />,
    title: "Negotiate as One",
    body: "Approach the seller together and leverage your collective buying power to negotiate the best possible deal.",
  },
];

export default function HowItWorksPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand" data-testid="how-eyebrow">
        How BHEGA Works
      </div>
      <h1 className="t-page mt-2" data-testid="how-page-h1">
        How <span className="bg-[#FFBE06] text-black px-2">BHEGA Works</span>
      </h1>

      <ol className="mt-12 relative" data-testid="how-steps-list">
        {/* Vertical accent rail */}
        <span aria-hidden className="hidden sm:block absolute left-[22px] top-2 bottom-2 w-px bg-[#E5E5E5]" />

        {STEPS.map((s, idx) => (
          <li
            key={idx}
            className="relative flex gap-4 sm:gap-6 pb-8 last:pb-0"
            data-testid={`how-step-${idx + 1}`}
          >
            {/* Numbered medallion */}
            <div className="relative shrink-0">
              <div className="w-11 h-11 rounded-full bg-card text-brand flex items-center justify-center font-heading font-extrabold text-lg">
                {idx + 1}
              </div>
            </div>

            <div className="flex-1 bhega-card p-5 sm:p-6 -mt-1">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-none bg-[#FFBE06] text-black inline-flex items-center justify-center shrink-0">
                  {s.icon}
                </span>
                <h2 className="font-heading font-bold text-lg sm:text-xl text-foreground" data-testid={`how-step-title-${idx + 1}`}>
                  {s.title}
                </h2>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed" data-testid={`how-step-body-${idx + 1}`}>
                {s.body}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-12">
        <Link to="/browse" className="btn-primary" data-testid="how-cta-explore">
          Start exploring deals <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
